import XLSX from 'xlsx-js-style';
import { getMetricTotal } from './mrpStatisticsMetrics';

const COLORS = {
  header: 'FFE0B2',
  white: 'FFFFFF',
  total: 'FFF3E0',
  section: 'FFCC80',
};

const BORDER_ALL = {
  top: { style: 'thin', color: { rgb: '000000' } },
  bottom: { style: 'thin', color: { rgb: '000000' } },
  left: { style: 'thin', color: { rgb: '000000' } },
  right: { style: 'thin', color: { rgb: '000000' } },
};

function sanitizeFilePart(value) {
  return String(value || '')
    .replace(/[/\\:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'статистика';
}

function getMoscowNowStrings() {
  const now = new Date();
  const date = now.toLocaleDateString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = now.toLocaleTimeString('ru-RU', {
    timeZone: 'Europe/Moscow',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return { date, time };
}

function cellStyle({
  bold = false,
  fill = COLORS.white,
  align = 'center',
} = {}) {
  return {
    font: { name: 'Calibri', sz: 11, bold },
    fill: { patternType: 'solid', fgColor: { rgb: fill } },
    alignment: { horizontal: align, vertical: 'center', wrapText: true },
    border: BORDER_ALL,
  };
}

function setCell(ws, row, col, value, style) {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });
  ws[ref] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
}

function formatServerLabel(server) {
  if (!server) return '—';
  return String(server).replace('_', ' · ');
}

function formatMetricValue(metric, value, formatDuration) {
  if (metric.format === 'duration') return formatDuration(value || 0);
  return value > 0 ? value : '—';
}

function getMetricTotalFromWeek(weekStats, metric) {
  if (metric.key === 'minutes') return weekStats.totalMinutes || 0;
  if (weekStats.totalMetrics?.[metric.key] != null) {
    return weekStats.totalMetrics[metric.key];
  }
  return (weekStats.dailyStats || []).reduce(
    (sum, day) => sum + (Number(day[metric.key]) || 0),
    0
  );
}

/**
 * Экспорт недели одного менеджера в Excel (режим списком).
 */
export function exportMrpWeekToExcel({
  weekStats,
  metrics,
  formatDuration,
  formatDayName,
  formatDayDate,
  weekLabel,
}) {
  if (!weekStats?.dailyStats?.length || !metrics?.length) return false;

  const mod = weekStats.moderator || {};
  const nick = mod.nickname || 'Менеджер';
  const role = mod.role || '';
  const server = (mod.server || '').replace('_', ' · ');
  const title = [
    nick,
    role,
    server,
    weekLabel ? `неделя ${weekLabel}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const headers = ['День', 'Дата', ...metrics.map((m) => m.label)];
  const colCount = headers.length;
  const ws = {};

  setCell(ws, 0, 0, title, cellStyle({ bold: true, fill: COLORS.header, align: 'left' }));
  headers.forEach((header, col) => {
    setCell(ws, 1, col, header, cellStyle({ bold: true, fill: COLORS.header }));
  });

  weekStats.dailyStats.forEach((dayStat, index) => {
    const row = index + 2;
    const fill = COLORS.white;
    setCell(ws, row, 0, formatDayName(dayStat.day), cellStyle({ bold: true, fill }));
    setCell(ws, row, 1, formatDayDate(dayStat.date), cellStyle({ fill }));
    metrics.forEach((metric, metricIndex) => {
      const raw = metric.key === 'minutes'
        ? (dayStat.minutes || 0)
        : (Number(dayStat[metric.key]) || 0);
      setCell(
        ws,
        row,
        metricIndex + 2,
        formatMetricValue(metric, raw, formatDuration),
        cellStyle({ fill })
      );
    });
  });

  const totalRow = 2 + weekStats.dailyStats.length;
  setCell(ws, totalRow, 0, '', cellStyle({ fill: COLORS.total }));
  setCell(ws, totalRow, 1, 'Итого', cellStyle({ bold: true, fill: COLORS.total, align: 'right' }));
  metrics.forEach((metric, metricIndex) => {
    const total = getMetricTotalFromWeek(weekStats, metric);
    setCell(
      ws,
      totalRow,
      metricIndex + 2,
      formatMetricValue(metric, total, formatDuration),
      cellStyle({ bold: true, fill: COLORS.total })
    );
  });

  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: totalRow, c: colCount - 1 },
  });
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
  ws['!cols'] = [
    { wch: 14 },
    { wch: 12 },
    ...metrics.map((m) => ({ wch: Math.max(12, String(m.label).length + 2) })),
  ];
  ws['!freeze'] = {
    xSplit: 0,
    ySplit: 2,
    topLeftCell: 'A3',
    activePane: 'bottomLeft',
    state: 'frozen',
  };
  ws['!rows'] = [{ hpt: 26 }, { hpt: 22 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sanitizeFilePart(nick).slice(0, 31));

  const { date, time } = getMoscowNowStrings();
  const fileName = `${sanitizeFilePart(`${nick} ${weekLabel || 'неделя'}`)} — ${date} ${time.replace(/:/g, '-')}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return true;
}

function buildSectionSheet({
  title,
  moderators,
  metrics,
  formatDuration,
  weekLabel,
}) {
  const headers = ['Сервер', 'Никнейм', 'Роль', ...metrics.map((m) => m.label)];
  const colCount = headers.length;
  const ws = {};

  const headTitle = [title, weekLabel ? `неделя ${weekLabel}` : null]
    .filter(Boolean)
    .join(' · ');

  setCell(ws, 0, 0, headTitle, cellStyle({ bold: true, fill: COLORS.section, align: 'left' }));
  headers.forEach((header, col) => {
    setCell(ws, 1, col, header, cellStyle({ bold: true, fill: COLORS.header }));
  });

  moderators.forEach((mod, index) => {
    const row = index + 2;
    setCell(ws, row, 0, formatServerLabel(mod.server), cellStyle({ align: 'center' }));
    setCell(ws, row, 1, mod.nickname || '—', cellStyle({ bold: true, align: 'center' }));
    setCell(ws, row, 2, mod.role || '—', cellStyle({ align: 'center' }));
    metrics.forEach((metric, metricIndex) => {
      const total = getMetricTotal(mod, metric.key);
      setCell(
        ws,
        row,
        metricIndex + 3,
        formatMetricValue(metric, total, formatDuration),
        cellStyle({})
      );
    });
  });

  const lastRow = 1 + Math.max(moderators.length, 1);
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastRow, c: colCount - 1 },
  });
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
  ws['!cols'] = [
    { wch: 16 },
    { wch: 18 },
    { wch: 16 },
    ...metrics.map((m) => ({ wch: Math.max(12, String(m.label).length + 2) })),
  ];
  ws['!freeze'] = {
    xSplit: 2,
    ySplit: 2,
    topLeftCell: 'C3',
    activePane: 'bottomRight',
    state: 'frozen',
  };
  ws['!rows'] = [{ hpt: 26 }, { hpt: 22 }];
  return ws;
}

/**
 * Экспорт табличного вида: все модераторы по секциям (Manager / Senior).
 */
export function exportMrpWeekTableToExcel({
  sections,
  formatDuration,
  weekLabel,
}) {
  const usable = (sections || []).filter(
    (section) => section?.moderators?.length && section?.metrics?.length
  );
  if (!usable.length) return false;

  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  usable.forEach((section) => {
    let sheetName = sanitizeFilePart(section.title || 'Лист').slice(0, 31);
    let suffix = 2;
    while (usedNames.has(sheetName)) {
      const base = sanitizeFilePart(section.title || 'Лист').slice(0, 28);
      sheetName = `${base} ${suffix}`.slice(0, 31);
      suffix += 1;
    }
    usedNames.add(sheetName);

    const ws = buildSectionSheet({
      title: section.title || 'Секция',
      moderators: section.moderators,
      metrics: section.metrics,
      formatDuration,
      weekLabel,
    });
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  const { date, time } = getMoscowNowStrings();
  const fileName = `${sanitizeFilePart(`MRP таблица ${weekLabel || 'неделя'}`)} — ${date} ${time.replace(/:/g, '-')}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return true;
}
